import React, { useState, useEffect } from "react";
import { useAppContext } from "../context/AppContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Upload,
  Trash2,
  Download,
  Eye,
  AlertCircle,
  Settings,
  X,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Spinner } from "../components/ui/spinner";
import {
  listOnboardingFiles,
  uploadOnboardingFile,
  deleteOnboardingFile,
  downloadOnboardingFile,
  processOnboardingFile,
} from "../services/api";

function OnboardingFilesPage() {
  const { actions } = useAppContext();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processingFile, setProcessingFile] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);
  const [processingResult, setProcessingResult] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listOnboardingFiles();
      const filesData = response?.data || [];
      console.log("Files data:", filesData);
      setFiles(Array.isArray(filesData) ? filesData : []);
    } catch (err) {
      console.error("Error loading files:", err);
      setError(err.message || "Error loading files");
      actions.setNotification(err.message || "Error loading files", "error");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        actions.setNotification("Only PDF files are allowed", "error");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      actions.setNotification("Please select a file first", "error");
      return;
    }

    setUploading(true);
    setError(null);
    try {
      await uploadOnboardingFile(selectedFile);
      actions.setNotification("File uploaded successfully", "success");
      setSelectedFile(null);
      loadFiles();
    } catch (err) {
      console.error("Error uploading file:", err);
      setError(err.message || "Error uploading file");
      actions.setNotification(err.message || "Error uploading file", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileName) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await deleteOnboardingFile(fileName);
      actions.setNotification("File deleted successfully", "success");
      loadFiles();
    } catch (err) {
      console.error("Error deleting file:", err);
      setError(err.message || "Error deleting file");
      actions.setNotification(err.message || "Error deleting file", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await downloadOnboardingFile(file.name);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading file:", err);
      actions.setNotification(err.message || "Error downloading file", "error");
    }
  };

  const handleView = async (file) => {
    try {
      const response = await downloadOnboardingFile(file.name);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      setViewingFile({ name: file.name, url });
    } catch (err) {
      console.error("Error viewing file:", err);
      actions.setNotification(err.message || "Error viewing file", "error");
    }
  };

  const handleProcess = async (file) => {
    setProcessingFile(file.name);
    try {
      // First, download the file
      const downloadResponse = await downloadOnboardingFile(file.name);
      const blob = downloadResponse.data;
      const pdfFile = new File([blob], file.name, { type: "application/pdf" });

      // Process the invoice
      const result = await processOnboardingFile(pdfFile);
      setProcessingResult(result);
      actions.setNotification("Invoice processed successfully", "success");
    } catch (err) {
      console.error("Error processing invoice:", err);
      actions.setNotification(
        err.message || "Error processing invoice",
        "error"
      );
    } finally {
      setProcessingFile(null);
    }
  };

  const loadPreview = async (file) => {
    if (previewUrls[file.name]) return;

    try {
      const response = await downloadOnboardingFile(file.name);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      setPreviewUrls((prev) => ({ ...prev, [file.name]: url }));
    } catch (err) {
      console.error("Error loading preview:", err);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => {
        window.URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>Onboarding Files</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="mb-6">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label htmlFor="file">Select PDF File</Label>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
              </div>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="mt-6"
              >
                {uploading ? (
                  <Spinner className="mr-2" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Upload
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Spinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {files.map((file) => (
                <Card key={file.name} className="overflow-hidden">
                  <div
                    className="relative aspect-[3/4] bg-gray-100 cursor-pointer"
                    onClick={() => loadPreview(file)}
                  >
                    {previewUrls[file.name] ? (
                      <iframe
                        src={`${previewUrls[file.name]}#toolbar=0&navpanes=0`}
                        className="w-full h-full"
                        title={`Preview of ${file.name}`}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <Eye className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-500">
                            Click to preview
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200" />
                  </div>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <h3
                        className="font-medium text-sm truncate"
                        title={file.name}
                      >
                        {file.name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {new Date(file.lastModified).toLocaleString()}
                      </p>
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleView(file)}
                          title="View"
                          className="h-8 w-8"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDownload(file)}
                          title="Download"
                          className="h-8 w-8"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleProcess(file)}
                          disabled={processingFile === file.name}
                          title="Process Invoice"
                          className="h-8 w-8"
                        >
                          {processingFile === file.name ? (
                            <Spinner className="h-4 w-4" />
                          ) : (
                            <Settings className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => handleDelete(file.name)}
                          title="Delete"
                          className="h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {files.length === 0 && (
                <div className="col-span-full text-center text-gray-500 py-8">
                  No files found
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Modal */}
      {viewingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-11/12 h-5/6 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-lg font-semibold truncate max-w-[80%]"
                title={viewingFile.name}
              >
                {viewingFile.name}
              </h3>
              <button
                onClick={() => setViewingFile(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={viewingFile.url}
                className="w-full h-full"
                title="PDF Viewer"
              />
            </div>
          </div>
        </div>
      )}

      {/* Processing Result Modal */}
      {processingResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-11/12 h-5/6 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Processing Results</h3>
              <button
                onClick={() => setProcessingResult(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <pre className="bg-gray-100 p-4 rounded-lg text-sm">
                {JSON.stringify(processingResult, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnboardingFilesPage;
